import { LayoutGroup } from 'framer-motion';
import { MODULE_BY_SIDEBAR_LABEL } from '@constants/modules';
import LinksGroup from './links-group';
import {
  SidebarBrandHeader,
  SidebarFooter,
  useSidebarIdentity,
  useSidebarMenus,
  useSidebarSurface,
} from './sidebar-v2';

export interface SidebarNavProps {
  /** Untranslated group label to hold open; every other group stays closed. */
  forceOpenGroup?: string;
  /** Sub-link `link` to show as active; omitted or '' shows none. */
  forceActiveLink?: string;
  /** Non-interactive: no clicks, no focus, hidden from assistive tech. */
  inert?: boolean;
  /** Rail width in px; the live rail is 240 at 100% text scale. */
  width?: number;
  /**
   * Namespaces framer-motion's shared `layoutId`s. Without it the active
   * column would animate between this copy and the live rail.
   */
  layoutGroupId?: string;
  /** Render the rail's footer block (account, theme, Pengaturan, help, language, logout, collapse). */
  showFooter?: boolean;
  /** Paint a footer row in its hover state (the film points at Pengaturan). */
  footerHighlight?: 'pengaturan';
}

/**
 * The rail's header, menu list and (optionally) footer, rendered from the
 * same identity, filters, surface and row components the live sidebar uses;
 * only the quick search is left out. It exists for the onboarding film: the
 * film scripts THIS component rather than a drawing of it, so what it shows
 * is what the user will see.
 */
export default function SidebarNav({
  forceOpenGroup,
  forceActiveLink,
  inert = false,
  width = 240,
  layoutGroupId = 'sidebar-nav-preview',
  showFooter = false,
  footerHighlight,
}: SidebarNavProps) {
  const { userEmail, professionalModeEnabled, canImpersonate, isImpersonating } = useSidebarIdentity();
  const { filteredMenus, isFeatureEnabled } = useSidebarMenus({ userEmail, professionalModeEnabled });
  const { sidebarStyle } = useSidebarSurface();
  const isGatewayUser = userEmail.toLowerCase().includes('gateway');

  return (
    <div
      className="h-full flex flex-col jv-glass-surface"
      style={{ ...sidebarStyle, width, minWidth: width }}
      inert={inert || undefined}
      aria-hidden={inert || undefined}
    >
      <SidebarBrandHeader isMinimized={false} isGatewayUser={isGatewayUser} />
      <div className="relative flex-1 overflow-hidden px-2.5 py-2" data-sidebar-nav-list>
        <div className="space-y-0.5">
          <LayoutGroup id={layoutGroupId}>
            {filteredMenus.map((item) => {
              const moduleCode = MODULE_BY_SIDEBAR_LABEL[item.label];
              const locked = moduleCode ? !isFeatureEnabled(moduleCode) : false;
              return (
                // `data-nav-group` rather than `data-tour`: a second `nav-master`
                // in the document would be what a running tour's querySelector
                // finds first, and this copy is not the one it means.
                <div key={item.label} data-nav-group={item.label}>
                  <LinksGroup
                    {...item}
                    isMinimized={false}
                    locked={locked}
                    forceOpened={item.label === forceOpenGroup}
                    forceActiveSubLink={forceActiveLink ?? ''}
                  />
                </div>
              );
            })}
          </LayoutGroup>
        </div>
      </div>
      {showFooter && (
        <SidebarFooter
          isMinimized={false}
          userEmail={userEmail}
          canImpersonate={canImpersonate}
          isImpersonating={isImpersonating}
          highlight={footerHighlight}
          tourAnchors={false}
        />
      )}
    </div>
  );
}
